/**
 * Domain vocabulary for Slate.
 *
 * This module is the single source of truth for what a board *is*. It is
 * imported by the Worker, by the Durable Object and by the browser bundle, so
 * it must not reference any runtime-specific global: no `D1Database`, no `WebSocket`,
 * no `document`. Only plain types and `as const` arrays live here.
 *
 * Two conventions are worth knowing before reading further:
 *
 * 1. Identifiers are opaque strings. They are generated by `crypto.randomUUID()`
 *    in the Worker, which means a client can also mint one for an optimistic
 *    insert without a round trip - the server accepts a client-supplied id only
 *    where the protocol says so, and always as a hint, never as authority.
 *
 * 2. Timestamps are ISO-8601 strings in UTC with millisecond precision
 *    (`2026-01-04T09:31:00.123Z`), not epoch integers. D1 stores them as TEXT
 *    and SQLite's `datetime()` comparisons on this format are lexicographic and
 *    therefore correct, while an integer would need conversion at every
 *    boundary and would be unreadable in the D1 console during an incident.
 */

/** Opaque UUIDv4 string. Aliased rather than branded: branding leaks into every
 * call site for no real safety gain, since the only way to get one wrong is to
 * pass the wrong variable, which the parameter names already guard against. */
export type Id = string;

export type UserId = Id;
export type WorkspaceId = Id;
export type BoardId = Id;
export type ColumnId = Id;
export type CardId = Id;
export type CommentId = Id;
export type AttachmentId = Id;
export type LabelId = Id;
export type SessionId = Id;

/** ISO-8601 UTC timestamp with milliseconds. */
export type Timestamp = string;

/** Calendar date without a time component, `YYYY-MM-DD`. Due dates are dates,
 * not instants: "due Friday" must not shift when a viewer is in another
 * timezone, which is exactly what would happen if this were a Timestamp. */
export type DateOnly = string;

// ---------------------------------------------------------------------------
// Roles and permissions
// ---------------------------------------------------------------------------

export const ROLES = ['owner', 'admin', 'member', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

/**
 * Permissions are modelled as a flat capability list rather than as a role
 * hierarchy comparison (`role >= 'member'`). Ordering comparisons break the
 * moment a role is added that is not a strict superset of the one below it,
 * and the failure is silent. A capability table makes the intent explicit and
 * lets the client hide a control using the exact same predicate the server
 * enforces, so the UI can never offer an action that will 403.
 */
export const PERMISSIONS = [
  'workspace:read',
  'workspace:update',
  'workspace:invite',
  'workspace:remove_member',
  'workspace:change_role',
  'workspace:delete',
  'board:create',
  'board:read',
  'board:update',
  'board:delete',
  'card:write',
  'comment:write',
  'attachment:write',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Role to capability mapping. `owner` is the only role that can delete a
 * workspace or transfer it; `admin` can do everything else that changes the
 * workspace. `viewer` is deliberately read-only down to comments and
 * attachments - a viewer who can comment is a member, and conflating the two
 * makes the role list meaningless.
 */
const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  owner: PERMISSIONS,
  admin: [
    'workspace:read',
    'workspace:update',
    'workspace:invite',
    'workspace:remove_member',
    'workspace:change_role',
    'board:create',
    'board:read',
    'board:update',
    'board:delete',
    'card:write',
    'comment:write',
    'attachment:write',
  ],
  member: ['workspace:read', 'board:create', 'board:read', 'board:update', 'card:write', 'comment:write', 'attachment:write'],
  viewer: ['workspace:read', 'board:read'],
};

export function can(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function permissionsFor(role: Role): readonly Permission[] {
  return ROLE_PERMISSIONS[role];
}

/** Roles a given role is allowed to assign. An admin must not be able to
 * promote themselves to owner, and must not be able to demote the owner. */
export function assignableRoles(actor: Role): readonly Role[] {
  switch (actor) {
    case 'owner':
      return ['owner', 'admin', 'member', 'viewer'];
    case 'admin':
      return ['admin', 'member', 'viewer'];
    case 'member':
    case 'viewer':
      return [];
  }
}

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

export interface User {
  id: UserId;
  email: string;
  name: string;
  /** Hex colour used for the presence avatar and the live cursor label. Stored
   * per user rather than derived from the id so a person can change it, and so
   * two users in the same room are not assigned colliding colours by a hash. */
  colour: string;
  avatarUrl: string | null;
  createdAt: Timestamp;
}

/** A user as seen by someone else. Never carries `email` unless the viewer is
 * a workspace admin: leaking member email addresses to a viewer is a real
 * privacy bug that is easy to introduce by returning the full row. */
export interface PublicUser {
  id: UserId;
  name: string;
  colour: string;
  avatarUrl: string | null;
}

export interface Workspace {
  id: WorkspaceId;
  slug: string;
  name: string;
  createdBy: UserId;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface WorkspaceMember {
  workspaceId: WorkspaceId;
  user: PublicUser;
  role: Role;
  createdAt: Timestamp;
}

export interface WorkspaceWithMembership extends Workspace {
  role: Role;
  memberCount: number;
}

export interface Board {
  id: BoardId;
  workspaceId: WorkspaceId;
  name: string;
  description: string | null;
  createdBy: UserId;
  archivedAt: Timestamp | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export const PRIORITIES = ['none', 'low', 'medium', 'high', 'urgent'] as const;
export type Priority = (typeof PRIORITIES)[number];

export interface Column {
  id: ColumnId;
  boardId: BoardId;
  name: string;
  /** Fractional index. See docs/ARCHITECTURE.md, "Fractional ordering". */
  position: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface Card {
  id: CardId;
  boardId: BoardId;
  columnId: ColumnId;
  title: string;
  description: string | null;
  assigneeId: UserId | null;
  dueDate: DateOnly | null;
  priority: Priority;
  /** Fractional index, scoped to `columnId`. */
  position: number;
  /** Optimistic concurrency counter. Bumped on every accepted write. */
  version: number;
  archivedAt: Timestamp | null;
  createdBy: UserId;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  labels: Label[];
}

export interface Label {
  id: LabelId;
  boardId: BoardId;
  name: string;
  colour: string;
}

export interface CardComment {
  id: CommentId;
  cardId: CardId;
  boardId: BoardId;
  author: PublicUser;
  body: string;
  editedAt: Timestamp | null;
  deletedAt: Timestamp | null;
  createdAt: Timestamp;
}

export interface CardAttachment {
  id: AttachmentId;
  cardId: CardId;
  boardId: BoardId;
  uploaderId: UserId;
  /** Storage key, not a URL. The client builds `/api/media/<key>` from it so
   * that changing the storage driver never changes the API surface. */
  assetKey: string;
  filename: string;
  contentType: string;
  byteSize: number;
  createdAt: Timestamp;
}

// ---------------------------------------------------------------------------
// Activity feed
// ---------------------------------------------------------------------------

export const BOARD_EVENT_TYPES = [
  'board.created',
  'board.updated',
  'board.archived',
  'column.created',
  'column.updated',
  'column.reordered',
  'column.deleted',
  'card.created',
  'card.moved',
  'card.updated',
  'card.archived',
  'card.deleted',
  'comment.created',
  'comment.updated',
  'comment.deleted',
  'attachment.created',
  'member.joined',
] as const;

export type BoardEventType = (typeof BOARD_EVENT_TYPES)[number];

/**
 * One row of the append-only activity log, and simultaneously the wire format
 * of a realtime broadcast. Making the persisted row and the broadcast payload
 * the same shape is intentional: it means the reconnect replay path
 * (`GET /state?since=`) and the live WebSocket path deliver byte-identical
 * objects, so the client needs exactly one reducer instead of two that drift.
 */
export interface BoardEvent {
  id: Id;
  boardId: BoardId;
  /** Monotonic per board, assigned by the Durable Object. Dense: a board's
   * events are 1..N with no gaps, which is what makes `?since=` a correct
   * resync cursor rather than an approximation. */
  sequence: number;
  type: BoardEventType;
  actorId: UserId | null;
  /** The card this event concerns, or `null` for board, column and membership
   * events. Denormalised out of the payload so the card history panel is an
   * indexed query rather than a JSON scan. */
  cardId: CardId | null;
  payload: Record<string, unknown>;
  createdAt: Timestamp;
}

/** Client-side identifier for an optimistic mutation. Echoed back by the server
 * on the resulting event so the client can retire the pending entry. */
export type OpId = string;

// ---------------------------------------------------------------------------
// Snapshots
// ---------------------------------------------------------------------------

/**
 * The full board payload. `sequence` is the board's event counter at the moment
 * the snapshot was taken; the client stores it and sends it back on reconnect.
 */
export interface BoardSnapshot {
  board: Board;
  columns: Column[];
  cards: Card[];
  labels: Label[];
  members: WorkspaceMember[];
  /** Derived, not stored: the highest `position` per column is useful to the
   * client for append-to-tail without scanning, and computing it server-side
   * avoids shipping the whole card list to a client that only wants to append. */
  sequence: number;
}

export interface BoardDelta {
  sequence: number;
  events: BoardEvent[];
}

// ---------------------------------------------------------------------------
// Presence
// ---------------------------------------------------------------------------

export interface CursorPosition {
  /** Normalised 0..1 against the board surface, not pixels. Pixels would be
   * meaningless to a peer whose viewport is a different size or who has
   * scrolled horizontally, and normalising is one division on each side. */
  x: number;
  y: number;
}

export interface Presence {
  /** Per-connection, not per-user. The same account open in two tabs is two
   * presences, which is what makes the live cursor list correct. */
  clientId: string;
  userId: UserId;
  name: string;
  colour: string;
  cursor: CursorPosition | null;
  /** Client clock at last cursor update, used to expire a stale cursor if a
   * socket dies without a close frame. */
  lastSeenAt: number;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline';

// ---------------------------------------------------------------------------
// Ordering
// ---------------------------------------------------------------------------

/**
 * Spacing constant for fractional indices. Two cards inserted at the same
 * position repeatedly will eventually converge to a gap smaller than
 * floating-point epsilon; `REBALANCE_EPSILON` is the threshold below which the
 * server refuses to subdivide and instead renumbers the column. See
 * `packages/shared/src/ordering.ts` and docs/ARCHITECTURE.md.
 */
export const POSITION_GAP = 1024;
export const REBALANCE_EPSILON = 1e-4;
